// Modello di "superserie di SESSIONE" — effimero, salvato in localStorage con la
// sessione di allenamento. NON tocca il DB: la colonna exercises.superset_group
// (definita dal coach nella scheda) viene solo IMPORTATA come seed all'avvio.
//
// Un gruppo = { id, memberIds: [exId, ...], restSeconds: number|null }.
// Funziona in modo identico per esercizi di scheda, extra e allenamento libero,
// perché ragiona solo sugli id degli esercizi in sessione.
//
// Decisioni di design (confermate con l'utente):
// - solo sessione (nessuna scrittura su DB / scheda originale);
// - gruppi da 2+ esercizi (N-ario) anche per la creazione live (PB-008);
// - se un esercizio ha più serie, prosegue da solo oltre i round condivisi;
// - PB-011: il recupero "tra i giri" è una PROPRIETÀ della superserie (tabella
//   workout_supersets), NON derivato dal primo esercizio. Assente/null/0 = nessun
//   recupero (nessun default 90). I rest_seconds individuali restano inattivi
//   mentre l'esercizio è in superserie;
// - statistiche/volume/storico invariati (ogni esercizio salva i propri log).

/**
 * Crea i gruppi di sessione dai soli esercizi di scheda con superset_group.
 * Raggruppa esercizi CONSECUTIVI che condividono la stessa lettera (A/B/C…),
 * come li ha definiti il coach.
 * PB-011: restSeconds del gruppo = recupero della superserie per quella lettera
 * (`supersetRestByLetter`, da workout_supersets); assente → null (non impostato).
 */
export const seedFromProgramExercises = (exercises, supersetRestByLetter) => {
  if (!exercises || exercises.length === 0) return [];
  const groups = [];
  let current = null;
  let currentLetter = null;
  exercises.forEach((ex) => {
    if (ex.superset_group) {
      if (!current || currentLetter !== ex.superset_group) {
        currentLetter = ex.superset_group;
        current = {
          id: `seed-${ex.superset_group}`,
          memberIds: [ex.id],
          restSeconds: supersetRestByLetter?.[ex.superset_group] ?? null
        };
        groups.push(current);
      } else {
        current.memberIds.push(ex.id);
      }
    } else {
      current = null;
      currentLetter = null;
    }
  });
  // Tiene solo i gruppi reali (≥2 membri)
  return groups.filter((g) => g.memberIds.length >= 2);
};

/** Ritorna il gruppo che contiene exerciseId, o null. */
export const getSupersetForExercise = (sessionSupersets, exerciseId) =>
  (sessionSupersets || []).find((g) => g.memberIds.includes(exerciseId)) || null;

/** True se exerciseId è l'ultimo membro (in ordine) del proprio gruppo. */
export const isLastMember = (group, exerciseId) =>
  !!group && group.memberIds[group.memberIds.length - 1] === exerciseId;

/** Numero di round "condivisi" = minimo delle serie tra i membri del gruppo. */
export const sharedRounds = (group, exerciseStates) => {
  if (!group || !group.memberIds.length) return 0;
  const counts = group.memberIds.map((id) => exerciseStates?.[id]?.sets?.length || 0);
  return Math.min(...counts);
};

/**
 * True se il round (0-based) è completo per TUTTI i membri del gruppo
 * (cioè la serie #roundIndex di ognuno è spuntata).
 */
export const isRoundComplete = (group, exerciseStates, roundIndex) => {
  if (!group) return false;
  return group.memberIds.every((id) => {
    const sets = exerciseStates?.[id]?.sets || [];
    const s = sets[roundIndex];
    return !!(s && s.completed);
  });
};

/** Recupero (secondi) da usare dopo un round completo. */
export const resolveRestSeconds = (group) => group?.restSeconds ?? null;

/**
 * Collega N (≥2) esercizi in una superserie (PB-008). Rimuove eventuali
 * appartenenze precedenti degli id (un esercizio sta in un solo gruppo) e
 * scarta gruppi rimasti < 2 membri. L'ordine di memberIds è quello ricevuto
 * (il chiamante lo passa già ordinato secondo l'ordine reale della lista).
 * restSeconds: PB-011 — i gruppi live nascono con null (nessun default 90).
 * idFactory: funzione che genera l'id del nuovo gruppo.
 */
export const linkMembers = (sessionSupersets, memberIds, restSeconds, idFactory) => {
  const ids = (memberIds || []).filter(Boolean);
  if (ids.length < 2) return sessionSupersets || [];
  const cleaned = (sessionSupersets || [])
    .map((g) => ({ ...g, memberIds: g.memberIds.filter((id) => !ids.includes(id)) }))
    .filter((g) => g.memberIds.length >= 2);
  const id = idFactory ? idFactory() : `ss-${ids.join('-')}`;
  return [...cleaned, { id, memberIds: [...ids], restSeconds: restSeconds ?? null }];
};

/** Scioglie un gruppo per id (mantiene i set già inseriti negli esercizi). */
export const unlink = (sessionSupersets, groupId) =>
  (sessionSupersets || []).filter((g) => g.id !== groupId);

/**
 * Costruisce l'elenco ordinato di gruppi di render a partire dagli esercizi in
 * sessione (in ordine) e dal modello superserie. Ogni voce è:
 *   { type: 'single', exercise } | { type: 'superset', group, exercises: [...] }
 * Il gruppo superserie viene reso alla posizione del PRIMO membro; gli altri
 * membri vengono saltati quando incontrati. Un gruppo con <2 membri presenti in
 * sessione viene degradato a singoli (robustezza).
 */
export const buildRenderGroups = (orderedExercises, sessionSupersets) => {
  const byId = {};
  (orderedExercises || []).forEach((e) => { byId[e.id] = e; });
  const rendered = new Set();
  const result = [];
  (orderedExercises || []).forEach((ex) => {
    if (rendered.has(ex.id)) return;
    const group = getSupersetForExercise(sessionSupersets, ex.id);
    if (group) {
      const members = group.memberIds.map((id) => byId[id]).filter(Boolean);
      if (members.length >= 2) {
        members.forEach((m) => rendered.add(m.id));
        result.push({ type: 'superset', group, exercises: members });
        return;
      }
    }
    rendered.add(ex.id);
    result.push({ type: 'single', exercise: ex });
  });
  return result;
};
